import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/common/Navbar';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Users from '../components/users/Users';
import AuditLogTab from '../components/users/AuditLogTab';
import MessengersTab from '../components/users/MessengersTab';
import RolesTab from '../components/users/RolesTab';
import { getUsers, createUser, updateUser, deleteUser, getRoles } from '../services/api';
import '../components/common/ui.css';
import './UsersPage.css';

const UsersPage = () => {
  const { user, isAuthenticated, loading: authLoading, logout } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Пользователи и роли грузятся вместе: в списке пользователей нужны
  // названия ролей, а во вкладке ролей — сколько на каждой людей.
  const loadUsers = useCallback(async () => {
    try {
      const [usersData, rolesData] = await Promise.all([getUsers(), getRoles()]);
      setUsers(usersData.users || []);
      setRoles(rolesData.roles || []);
      setPages(rolesData.pages || []);
      setError('');
    } catch (e) {
      setError('Не удалось загрузить пользователей и роли');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleCreate = async (payload) => {
    await createUser(payload);
    await loadUsers();
  };

  const handleUpdate = async (userId, payload) => {
    await updateUser(userId, payload);
    await loadUsers();
  };

  const handleDelete = async (userId) => {
    await deleteUser(userId);
    await loadUsers();
  };

  if (!isAuthenticated) return null;

  // Страница только для администратора — остальных возвращаем на дашборд
  if (!authLoading && user && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="users-page">
      <Navbar user={user} isAdmin={user?.role === 'admin'} onLogout={logout} />

      <nav className="ui-tabs" aria-label="Раздел" style={{ marginTop: 'var(--s-5)', marginBottom: 'var(--s-4)' }}>
        <button type="button" className={`ui-tab ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>
          Пользователи
        </button>
        <button type="button" className={`ui-tab ${tab === 'roles' ? 'active' : ''}`} onClick={() => setTab('roles')}>
          Роли и доступ
        </button>
        <button type="button" className={`ui-tab ${tab === 'audit' ? 'active' : ''}`} onClick={() => setTab('audit')}>
          Журнал действий
        </button>
        <button type="button" className={`ui-tab ${tab === 'integrations' ? 'active' : ''}`} onClick={() => setTab('integrations')}>
          Интеграции
        </button>
      </nav>

      {(tab === 'users' || tab === 'roles') && (
        <>
          {loading && <LoadingSpinner />}

          {!loading && error && (
            <div className="users-page-error">
              {error}
              <button type="button" className="ui-btn-primary" onClick={loadUsers}>
                Попробовать снова
              </button>
            </div>
          )}

          {!loading && !error && tab === 'roles' && (
            <RolesTab roles={roles} pages={pages} onChanged={loadUsers} />
          )}

          {!loading && !error && tab === 'users' && (
            <Users
              users={users}
              roles={roles}
              currentUserId={user?.id}
              onCreate={handleCreate}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
            />
          )}
        </>
      )}

      {tab === 'audit' && <AuditLogTab />}

      {tab === 'integrations' && <MessengersTab />}
    </div>
  );
};

export default UsersPage;
